import { MaterialModule } from '../../material/material/material.module';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CommonModule, AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ClientSelectionService } from '../clients/client-selection.service';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { CommonserviceService } from '../../services/commonservice.service';
import { ApiService } from '../../services/api.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NavigationService } from '../../services/navigation.service';
import { Client } from '../../models/clients.model';
import { toggleAutocompletePanel } from '../../shared/utils/autocomplete-panel.utils';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  standalone: false,
})
export class HeaderComponent implements OnInit {
  @ViewChild('inputAutoComplete') inputAutoComplete: any;
  selectedClient: Client | null = null;
  isAuthenticated!: boolean;
  username?: string;
  results: any[] = [];
  isLoading = false;
  errorMessage = '';

  constructor(
    private clientService: ClientSelectionService,
    public _oktaStateService: OktaAuthStateService,
    @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth,
    private commonservice: CommonserviceService,
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private router: Router,
    @Inject(NavigationService) private navigationService: NavigationService
  ) {}

  myControl = new FormControl<string | Client>('');
  options: Client[] = [];
  filteredOptions!: Observable<Client[]>;
  searchBarWidth = '240px';

  async ngOnInit() {
    this.spinner.show();
    setTimeout(async () => {
      this.isAuthenticated = await this._oktaAuth.isAuthenticated();
      if (this.isAuthenticated) {
        const userClaims = await this._oktaAuth.getUser();
        this.username = userClaims['lastName'] + ', ' + userClaims['firstName'];
      }

      this.fetchData();
    }, 1500);
  }

  fetchData(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.apiService.getData('Clients').subscribe({
      next: (data) => {
        this.options = data;

        const longestLen = Math.max(...this.options.map(o => o.DisplayName?.length ?? 0), 15);
        this.searchBarWidth = (longestLen * 8 + 120) + 'px';

        this.filteredOptions = this.myControl.valueChanges.pipe(
          startWith(''),
          map((value) => {
            const name = typeof value === 'string' ? value : value?.DisplayName;
            return name ? this._filter(name as string) : this.options.slice();
          })
        );
        this.isLoading = false;
        this.spinner.hide();
      },
      error: (err) => {
        this.errorMessage = 'Failed to fetch data';
        console.error(err);
        this.isLoading = false;
      },
    });
  }

  clearInput(evt: any): void {
    evt.stopPropagation();
    this.myControl?.reset();
    this.inputAutoComplete?.nativeElement.focus();

    this.selectedClient = null;
    this.clientService.setSelectedClient(null);
    this.navigationService.setSelectedMenuItem('Home');
  }

  openOrClosePanel(evt: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(evt, trigger);
  }

  displayFn(user: Client): string {
    return user && user.DisplayName ? user.DisplayName : '';
  }

  isSelectedFederatedClient(): boolean {
    const selectedValue = this.myControl.value;
    return (
      typeof selectedValue !== 'string' && selectedValue?.OKTALoginType === 'F'
    );
  }

  private _filter(name: string): Client[] {
    const filterValue = name.toLowerCase();

    return this.options.filter((option) =>
      option.DisplayName.toLowerCase().includes(filterValue)
    );
  }

  onPanelOpened() {
    setTimeout(() => {
      this.inputAutoComplete.nativeElement.focus();
    });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    // console.log('Selected option:', event.option.value);
    this.selectedClient = event.option.value;
    this.clientService.setSelectedClient(event.option.value);
    this.navigationService.setSelectedMenuItem('Home');
  }

  logout() {
    this.commonservice.logout();
  }

  onInputChange() {
    const value = this.myControl.value;
    if (
      typeof value === 'string' &&
      !this.options.find((c) => c.DisplayName === value)
    ) {
      this.clientService.setSelectedClient(null);
      this.navigationService.setSelectedMenuItem('Home');
    }
  }
}